import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Clock, FileText, MessageSquare, Bell, Euro,
  Calendar, MapPin, User, Shield, Building, AlertCircle,
  Download, Send, Plus, Edit3, CheckCircle, XCircle,
  Users, ChevronDown, Save, X, Brain, Scale,
  BookOpen, ChevronRight, ExternalLink, Lock, Sparkles, Loader2,
  Pencil, Check, Copy, Trash2
} from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import SinistreTimeline from '../../../components/shared/SinistreTimeline'
import SinistreDocuments from '../../../components/shared/SinistreDocuments'
import AnalyseIABlock from '../../../components/sinistre/AnalyseIABlock'
import DeclarationAssureurBlock from '../../../components/sinistre/DeclarationAssureurBlock'
import DoctrineRecommandationBlock from '../../../components/sinistre/DoctrineRecommandationBlock'
import C1MemoirePanel from '../../../components/sinistre/C1MemoirePanel'
import ContactsDossier from '../../../components/sinistre/ContactsDossier'
import FlotteSinistreBlocks, { ExpertiseBlock } from '../../../components/sinistre/FlotteSinistreBlocks'
import ReglementAmiableBlock from '../../../components/sinistre/ReglementAmiableBlock'
import EligibiliteAmiableBlock from '../../../components/sinistre/EligibiliteAmiableBlock'
import AmiableSinistreView from '../../../components/sinistre/AmiableSinistreView'
import TransportSinistreBlocks from '../../../components/sinistre/TransportSinistreBlocks'
import IndemniteDiffereeBlock from '../../../components/sinistre/IndemniteDiffereeBlock'
import { PresenceBanner, ConflitEditionModal } from '../../../components/sinistre/PresenceConflit'
import InformationsImportantesBlock from '../../../components/sinistre/InformationsImportantesBlock'
import { RichNoteEditor, noteToPlainText } from '../../../components/shared/RichNote'
import { useSinistrePresence } from '../../../hooks/useSinistrePresence'
import { useBrouillonSinistre } from '../../../hooks/useBrouillonSinistre'
import { BrouillonBar } from '../../../components/sinistre/BrouillonBar'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Helper: create timeline event via backend API (replaces direct Supabase calls)
async function createEvent(sinistreId, eventData) {
  const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}/evenement`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(eventData),
  })
  if (!res.ok) throw new Error('Erreur creation evenement')
  return res.json()
}

function EditableNumeroDossier({ sinistreId, sinistre, onRefresh }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(sinistre?.numero_dossier_compagnie || '')
  const [saving, setSaving] = useState(false)
  const originalRef = React.useRef(sinistre?.numero_dossier_compagnie || '')

  useEffect(() => {
    setValue(sinistre?.numero_dossier_compagnie || '')
    originalRef.current = sinistre?.numero_dossier_compagnie || ''
  }, [sinistre?.numero_dossier_compagnie])

  async function handleAutoSave() {
    if (value === originalRef.current) { setEditing(false); return }
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_dossier_compagnie: value }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sauvegarde automatique')
        originalRef.current = value
        setEditing(false)
        if (onRefresh) onRefresh()
      } else { toast.error(data.detail || 'Erreur') }
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 py-1.5 pl-6" data-testid="edit-num-dossier">
        <Building size={14} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="N dossier compagnie"
          className="flex-1 text-xs border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          autoFocus
          onBlur={handleAutoSave}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setValue(originalRef.current); setEditing(false) } }}
          data-testid="num-dossier-input"
        />
        {saving && <Loader2 size={12} className="animate-spin text-indigo-500" />}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 py-1.5 pl-6 group cursor-pointer" onClick={() => setEditing(true)} data-testid="num-dossier-display">
      <Building size={14} className="text-slate-400 shrink-0" />
      <span className="text-xs text-slate-500 min-w-[90px]">N compagnie</span>
      <span className="text-xs font-medium text-slate-700 flex-1">
        {sinistre?.numero_dossier_compagnie || <span className="text-slate-300 italic">Cliquer pour ajouter</span>}
      </span>
      <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  )
}

function EditableDateRow({ icon, label, field, sinistre, sinistreId, onRefresh }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const originalRef = React.useRef('')

  function startEdit() {
    const raw = sinistre?.[field]
    let v = ''
    if (raw) {
      const d = new Date(raw)
      v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    }
    setValue(v)
    originalRef.current = v
    setEditing(true)
  }

  async function handleAutoSave() {
    if (!value || value === originalRef.current) { setEditing(false); return }
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sauvegarde automatique')
        setEditing(false)
        onRefresh?.()
      } else { toast.error(data.detail || 'Erreur') }
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }

  const raw = sinistre?.[field]
  const displayDate = raw
    ? new Date(raw).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '--'

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 py-1.5 pl-6" data-testid={`edit-date-${field}`}>
        <span className="text-slate-400 shrink-0">{icon}</span>
        <span className="text-xs text-slate-500 w-32">{label}</span>
        <input
          type="date"
          value={value}
          onChange={e => setValue(e.target.value)}
          autoFocus
          onBlur={handleAutoSave}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false) }}
          className="text-sm border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid={`date-input-${field}`}
        />
        {saving && <Loader2 size={12} className="animate-spin text-indigo-500" />}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 py-1.5 pl-6 group cursor-pointer" onClick={startEdit}
      data-testid={`date-display-${field}`}>
      <span className="text-slate-400 shrink-0">{icon}</span>
      <div>
        <div className="text-[11px] text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 font-medium flex items-center gap-1">
          {displayDate}
          <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </div>
  )
}


function EditableTextField({ icon, label, field, sinistre, sinistreId, onRefresh, colSpan }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const originalRef = React.useRef('')

  function startEdit() {
    const v = field === 'lieu' ? (sinistre?.[field] || sinistre?.lieu_sinistre?.adresse || '') : (sinistre?.[field] || '')
    setValue(v)
    originalRef.current = v
    setEditing(true)
  }

  async function handleAutoSave() {
    if (value === originalRef.current) { setEditing(false); return }
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sauvegarde automatique')
        setEditing(false)
        onRefresh?.()
      } else { toast.error(data.detail || 'Erreur') }
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }

  const displayValue = field === 'lieu' ? (sinistre?.[field] || sinistre?.lieu_sinistre?.adresse || '--') : (sinistre?.[field] || '--')

  if (editing) {
    return (
      <div className={`flex items-center gap-1.5 py-1.5 pl-6 ${colSpan ? 'col-span-2' : ''}`} data-testid={`edit-${field}`}>
        <span className="text-slate-400 shrink-0">{icon}</span>
        <span className="text-xs text-slate-500 w-12">{label}</span>
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          autoFocus
          placeholder={label}
          onBlur={handleAutoSave}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setValue(originalRef.current); setEditing(false) } }}
          className="flex-1 text-sm border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid={`text-input-${field}`}
        />
        {saving && <Loader2 size={12} className="animate-spin text-indigo-500" />}
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-1.5 py-1.5 pl-6 group cursor-pointer ${colSpan ? 'col-span-2' : ''}`}
      onClick={startEdit} data-testid={`text-display-${field}`}>
      <span className="text-slate-400 shrink-0">{icon}</span>
      <div>
        <div className="text-[11px] text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 font-medium flex items-center gap-1">
          {displayValue}
          <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </div>
  )
}


// ════════════════════════════════════════════════════════════════════════════
// BLOC CONTEXTE CONTRAT / POLICE (P2.1)
// ════════════════════════════════════════════════════════════════════════════

function ContratContextBlock({ sinistreId }) {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [openSection, setOpenSection] = useState('garanties')

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}/contexte-contrat`)
      if (res.ok) setData(await res.json())
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [sinistreId])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <Clock className="w-4 h-4 animate-spin mr-2" />
        Chargement du contexte contrat…
      </div>
    )
  }

  if (!data?.success) {
    return (
      <div className="text-center py-8 text-slate-400">
        <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">Aucun contexte contractuel disponible</p>
      </div>
    )
  }

  const { police, documents, resume_contractuel, police_specific_docs } = data
  const { garanties = [], franchises = [], plafonds = [], exclusions = [] } = resume_contractuel || {}
  const hasData = garanties.length > 0 || franchises.length > 0 || plafonds.length > 0 || exclusions.length > 0

  const sections = [
    { id: 'garanties', label: 'Garanties', items: garanties, color: 'emerald', icon: CheckCircle },
    { id: 'franchises', label: 'Franchises', items: franchises, color: 'blue', icon: Euro },
    { id: 'plafonds', label: 'Plafonds', items: plafonds, color: 'purple', icon: Shield },
    { id: 'exclusions', label: 'Exclusions', items: exclusions, color: 'red', icon: XCircle },
  ]

  const confidenceColor = (c) => c >= 0.8 ? 'text-emerald-600' : c >= 0.5 ? 'text-amber-600' : 'text-red-600'

  return (
    <div className="space-y-4" data-testid="contrat-context-block">
      {/* Police */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-indigo-500" />
              <span className="font-semibold text-slate-800">
                {police?.libelle || police?.numero_police || 'Police non renseignée'}
              </span>
              {police_specific_docs && (
                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                  Documents spécifiques à cette police
                </span>
              )}
              {!police_specific_docs && documents.length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  Documents généraux du tenant
                </span>
              )}
            </div>
            {police && (
              <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                <span><strong>N°</strong> {police.numero_police}</span>
                <span><strong>Type</strong> {police.type_police}</span>
                {police.compagnies && <span><strong>Compagnie</strong> {police.compagnies.nom}</span>}
                {police.date_effet && <span><strong>Effet</strong> {new Date(police.date_effet).toLocaleDateString('fr-FR')}</span>}
                {police.date_echeance && <span><strong>Échéance</strong> {new Date(police.date_echeance).toLocaleDateString('fr-FR')}</span>}
              </div>
            )}
          </div>
          {police && (
            <button
              onClick={() => navigate(`/c2/polices`)}
              className="shrink-0 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              data-testid="btn-voir-police"
            >
              <ExternalLink className="w-3 h-3" />
              Voir la police
            </button>
          )}
        </div>
      </div>

      {/* Documents KB */}
      {documents.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Documents contractuels ({documents.length})
          </p>
          <div className="space-y-1.5">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-100 rounded-lg text-sm">
                <BookOpen className="w-4 h-4 text-indigo-400 shrink-0" />
                <span className="font-medium text-slate-700 flex-1 truncate">{doc.nom}</span>
                <span className="text-xs text-slate-400">{doc.category}</span>
                {doc.version > 1 && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">v{doc.version}</span>}
                {doc.confidence > 0 && (
                  <span className={`text-xs font-medium ${confidenceColor(doc.confidence)}`}>
                    {Math.round(doc.confidence * 100)}%
                  </span>
                )}
                {doc.analyse_date && (
                  <span className="text-xs text-slate-400 hidden lg:block">
                    {new Date(doc.analyse_date).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {doc.extraction_status !== 'completed' && (
                  <span className="text-xs text-amber-500 italic">(pas encore extrait)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Données extraites */}
      {hasData ? (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Informations extraites
          </p>
          <div className="space-y-2">
            {sections.filter(s => s.items.length > 0).map(section => {
              const Icon = section.icon
              const isOpen = openSection === section.id
              const colorMap = {
                emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
                blue: 'bg-blue-50 border-blue-100 text-blue-700',
                purple: 'bg-purple-50 border-purple-100 text-purple-700',
                red: 'bg-red-50 border-red-100 text-red-700',
              }
              return (
                <div key={section.id} className={`rounded-lg border overflow-hidden ${colorMap[section.color]}`}>
                  <button
                    onClick={() => setOpenSection(isOpen ? null : section.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:opacity-80"
                    data-testid={`section-${section.id}`}
                  >
                    <span className="flex items-center gap-2 font-medium text-sm">
                      <Icon className="w-4 h-4" />
                      {section.label}
                      <span className="text-xs font-bold bg-white/60 px-1.5 py-0.5 rounded-full">
                        {section.items.length}
                      </span>
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="bg-white px-4 py-3 space-y-2">
                      {section.items.map((item, i) => (
                        <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
                          <div className="flex-1">
                            <p className="text-sm text-slate-800 font-medium">
                              {item.type || item.nom || item.description || item.garantie || JSON.stringify(item).slice(0, 80)}
                            </p>
                            {item.montant && (
                              <p className="text-xs text-slate-500">
                                Montant : {typeof item.montant === 'number' ? `${item.montant.toLocaleString('fr-FR')} €` : item.montant}
                              </p>
                            )}
                            {item.conditions && (
                              <p className="text-xs text-slate-400 italic mt-0.5">{item.conditions}</p>
                            )}
                          </div>
                          <div className="text-right text-xs text-slate-400 shrink-0">
                            <div>{item.doc_source}</div>
                            {item.confidence > 0 && (
                              <div className={confidenceColor(item.confidence)}>
                                {Math.round(item.confidence * 100)}%
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400">
          <Lock className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune donnée contractuelle extraite</p>
          <p className="text-xs mt-1">Lancez l'extraction IA sur les documents KB de ce client pour voir les garanties, franchises et exclusions.</p>
        </div>
      )}
    </div>
  )
}

const STATUT_OPTIONS = [
  { value: 'declare_c2', label: 'Déclaré C2', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  { value: 'attente_elements', label: 'Attente d\'éléments', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  { value: 'attente_ouverture_compagnie', label: 'Attente ouverture compagnie', color: 'bg-cyan-100 text-cyan-700', dot: 'bg-cyan-500' },
  { value: 'ouvert_compagnie', label: 'Ouvert compagnie', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { value: 'attente_compagnie_client', label: 'Attente compagnie/client', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { value: 'expertise_ou_sans', label: 'Expertise / Sans expertise', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  { value: 'attente_reglement', label: 'Attente règlement', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  { value: 'reglement_effectue', label: 'Règlement effectué', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  { value: 'cloture', label: 'Clôturé', color: 'bg-green-100 text-green-800', dot: 'bg-green-600' },
  // Legacy (backward display)
  { value: 'declare', label: 'Déclaré (ancien)', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  { value: 'en_attente_elements', label: 'Attente éléments (ancien)', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  { value: 'cloture_favorable', label: 'Clôturé (ancien)', color: 'bg-green-100 text-green-800', dot: 'bg-green-600' },
  { value: 'cloture_refus', label: 'Clôturé refus (ancien)', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  { value: 'sans_suite', label: 'Sans suite (ancien)', color: 'bg-slate-100 text-slate-500', dot: 'bg-slate-300' },
]

// Pipeline stages — 9 étapes exactes
const PIPELINE_STAGES = [
  { key: 'declare_c2', label: 'Déclaré C2', icon: FileText },
  { key: 'attente_elements', label: 'Attente éléments', icon: Clock },
  { key: 'attente_ouverture_compagnie', label: 'Att. ouv. cie', icon: Send },
  { key: 'ouvert_compagnie', label: 'Ouvert cie', icon: Building },
  { key: 'attente_compagnie_client', label: 'Att. cie/client', icon: Clock },
  { key: 'expertise_ou_sans', label: 'Expertise', icon: Shield },
  { key: 'attente_reglement', label: 'Att. règlement', icon: Euro },
  { key: 'reglement_effectue', label: 'Réglé', icon: CheckCircle },
  { key: 'cloture', label: 'Clôturé', icon: Lock },
]

// Map legacy statuses to closest new pipeline stage for display
const LEGACY_STAGE_MAP = {
  'declare': 0, 'en_attente_elements': 1, 'ouvert_assureur': 3, 'en_instruction': 3,
  'en_attente_compagnie': 4, 'expertise_en_cours': 5, 'expertise': 5,
  'negociation': 6, 'accord_recu': 7, 'paye': 7, 'provision': 6,
  'cloture_favorable': 8, 'cloture_refus': 8, 'sans_suite': 8,
}

const TYPE_SINISTRE_LABELS = {
  dab: 'Dommages aux biens',
  flotte: 'Flotte automobile',
  rc_tiers: 'Responsabilité civile',
  amiable: 'Règlement amiable',
  contentieux: 'Contentieux',
  autre: 'Autre',
}

const GRAVITE_CONFIG = {
  faible:   { label: 'Faible',   color: 'bg-green-100 text-green-700' },
  moyen:    { label: 'Moyen',    color: 'bg-amber-100 text-amber-700' },
  eleve:    { label: 'Élevé',    color: 'bg-orange-100 text-orange-700' },
  critique: { label: 'Critique', color: 'bg-red-100 text-red-700' },
}

export default function C2SinistreDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const versionRef = useRef(null)
  const autresPresents = useSinistrePresence(id, profile)
  const brouillon = useBrouillonSinistre(id, profile, versionRef, () => fetchSinistre())
  const { conflit, fermerConflit } = brouillon
  
  const [sinistre, setSinistre] = useState(null)
  const [events, setEvents] = useState([])
  const [documents, setDocuments] = useState([])
  const [relances, setRelances] = useState([])
  const [gestionnaires, setGestionnaires] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('timeline')
  
  // Edit mode states
  const [editMode, setEditMode] = useState(null) // 'statut' | 'provision' | 'gestionnaire'
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  
  // Note interne
  const [noteInterne, setNoteInterne] = useState('')
  const [sendingNote, setSendingNote] = useState(false)
  const [noteCouleur, setNoteCouleur] = useState('')
  
  // Relance modal
  const [showRelanceModal, setShowRelanceModal] = useState(false)
  const [relanceType, setRelanceType] = useState('compagnie')
  const [relanceSujet, setRelanceSujet] = useState('')
  const [relanceContenu, setRelanceContenu] = useState('')
  const [sendingRelance, setSendingRelance] = useState(false)
  const [generatingRelance, setGeneratingRelance] = useState(false)
  const [relanceContexte, setRelanceContexte] = useState('')

  // Lifecycle modals
  const [showPaiementModal, setShowPaiementModal] = useState(false)
  const [paiementForm, setPaiementForm] = useState({ montant: '', date: '', reference: '', commentaire: '' })
  const [savingPaiement, setSavingPaiement] = useState(false)
  const [showClotureModal, setShowClotureModal] = useState(false)
  const [clotureCommentaire, setClotureCommentaire] = useState('')
  const [savingCloture, setSavingCloture] = useState(false)

  async function handleDeleteSinistre() {
    const ok = window.confirm('Supprimer definitivement ce sinistre et toutes ses donnees (documents, analyses, evenements) ? Cette action est irreversible.')
    if (!ok) return
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${id}?user_id=${profile?.id || ''}`, { method: 'DELETE' })
      const data = await res.json()
      if (data.success) {
        toast.success('Sinistre supprime')
        navigate(-1)
      } else {
        toast.error(data.detail || data.message || 'Erreur suppression')
      }
    } catch (e) {
      console.error(e)
      toast.error('Erreur suppression')
    }
  }

  useEffect(() => {
    if (id) {
      fetchSinistre()
      fetchGestionnaires()
    }
  }, [id])

  async function fetchSinistre() {
    setLoading(true)
    try {
      // Use backend API (bypasses RLS) instead of direct Supabase client
      const res = await fetch(`${API_URL}/api/sinistres/${id}/detail`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const result = await res.json()
      
      if (!result.success || !result.sinistre) {
        throw new Error('Sinistre not found')
      }
      
      // Les saisies en brouillon (non encore envoyees) priment sur la version serveur affichee
      setSinistre({ ...result.sinistre, ...brouillon.draftRef.current })
      versionRef.current = result.sinistre.updated_at || null
      setEvents(result.events || [])
      setDocuments(result.documents || [])
      setRelances(result.relances || [])
    } catch (error) {
      console.error('Error fetching sinistre:', error)
      toast.error('Erreur lors du chargement du dossier')
    } finally {
      setLoading(false)
    }
  }

  async function fetchGestionnaires() {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/c2`)
      const data = await res.json()
      setGestionnaires(data?.users || [])
    } catch { setGestionnaires([]) }
  }

  async function handleUpdateStatut(newStatut) {
    if (newStatut === sinistre.statut) {
      setEditMode(null)
      return
    }
    
    setSaving(true)
    try {
      const oldStatut = sinistre.statut
      const isCloture = newStatut === 'cloture' || newStatut.startsWith('cloture_') || newStatut === 'sans_suite'
      
      // Use backend API for status update (handles DB constraint fallback)
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          statut: newStatut,
          date_cloture: isCloture ? new Date().toISOString() : null,
        })
      })
      
      if (!res.ok) {
        // If PATCH fails (constraint), try the lifecycle endpoint for known stages
        if (newStatut === 'ouvert_assureur') {
          const r2 = await fetch(`${API_URL}/api/sinistres/${id}/ouverture-assureur`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: profile.id })
          })
          if (!r2.ok) throw new Error('Erreur')
          toast.success('Statut mis à jour')
          setEditMode(null)
          fetchSinistre()
          return
        }
        throw new Error((await res.json().catch(() => ({}))).detail || 'Erreur mise à jour')
      }

      // Create event
      const newStatutLabel = STATUT_OPTIONS.find(s => s.value === newStatut)?.label || newStatut
      const oldStatutLabel = STATUT_OPTIONS.find(s => s.value === oldStatut)?.label || oldStatut
      
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'changement_statut',
        titre: 'Changement de statut',
        contenu: `Statut modifie de "${oldStatutLabel}" a "${newStatutLabel}"`,
        ancienne_valeur: { statut: oldStatut },
        nouvelle_valeur: { statut: newStatut },
        source: 'manuel',
      })

      toast.success('Statut mis à jour')
      setEditMode(null)
      fetchSinistre()
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateIndemniteImmediate() {
    const newValue = parseFloat(editValue)
    if (isNaN(newValue) || newValue < 0) {
      toast.error('Montant invalide')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ montant_rembourse: newValue }),
      })
      if (!res.ok) throw new Error('Erreur')
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'paiement',
        titre: 'Indemnité immédiate enregistrée',
        contenu: `Indemnité immédiate : ${formatCurrency(newValue)}`,
        ancienne_valeur: { montant_rembourse: sinistre.montant_rembourse },
        nouvelle_valeur: { montant_rembourse: newValue },
        source: 'manuel',
      })
      toast.success('Indemnité immédiate enregistrée')
      setEditMode(null)
      fetchSinistre()
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateProvision() {
    const newValue = parseFloat(editValue)
    if (isNaN(newValue) || newValue < 0) {
      toast.error('Montant invalide')
      return
    }
    
    setSaving(true)
    try {
      const oldValue = sinistre.provision_compagnie
      
      // Update provision_compagnie via backend API
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provision_compagnie: newValue, provision_source: 'gestionnaire' }),
      })
      if (!res.ok) throw new Error('Erreur')

      // Create event
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'provision_mise_a_jour',
        titre: 'Provision compagnie mise a jour',
        contenu: `Provision modifiee de ${formatCurrency(oldValue)} a ${formatCurrency(newValue)}`,
        ancienne_valeur: { provision_compagnie: oldValue },
        nouvelle_valeur: { provision_compagnie: newValue },
        source: 'manuel',
      })

      toast.success('Provision compagnie mise à jour')
      setEditMode(null)
      fetchSinistre()
    } catch (error) {
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateGestionnaire(gestionnaireId) {
    if (gestionnaireId === sinistre.gestionnaire_id) {
      setEditMode(null)
      return
    }
    
    setSaving(true)
    try {
      const newGest = gestionnaires.find(g => g.id === gestionnaireId)
      
      // Update sinistre via backend API
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gestionnaire_id: gestionnaireId || null }),
      })
      if (!res.ok) throw new Error('Erreur')

      // Create event
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'changement_statut',
        titre: gestionnaireId ? 'Gestionnaire assigne' : 'Gestionnaire retire',
        contenu: gestionnaireId 
          ? `Dossier assigne a ${newGest?.prenom} ${newGest?.nom}`
          : 'Gestionnaire retire du dossier',
        nouvelle_valeur: { gestionnaire_id: gestionnaireId },
        source: 'manuel',
      })

      toast.success(gestionnaireId ? 'Gestionnaire assigné' : 'Gestionnaire retiré')
      setEditMode(null)
      fetchSinistre()
    } catch (error) {
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  async function handleAddNoteInterne() {
    if (!noteToPlainText(noteInterne).trim()) return
    
    setSendingNote(true)
    try {
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'note_interne',
        titre: 'Note',
        contenu: noteInterne,
        source: 'manuel',
        nouvelle_valeur: noteCouleur ? { couleur: noteCouleur } : null,
      })

      setNoteInterne('')
      setNoteCouleur('')
      toast.success('Note ajoutee')
      fetchSinistre()
    } catch (error) {
      toast.error('Erreur lors de l\'ajout')
    } finally {
      setSendingNote(false)
    }
  }

  async function handleCreateRelance() {
    if (!relanceSujet.trim() || !relanceContenu.trim()) {
      toast.error('Veuillez remplir tous les champs')
      return
    }
    
    setSendingRelance(true)
    try {
      // Create relance via backend API
      const relanceRes = await fetch(`${API_URL}/api/sinistres/${id}/relance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: sinistre.tenant_id,
          user_id: profile.id,
          destinataire_type: relanceType,
          sujet: relanceSujet,
          contenu: relanceContenu,
        }),
      })
      if (!relanceRes.ok) throw new Error('Erreur relance')

      setShowRelanceModal(false)
      setRelanceSujet('')
      setRelanceContenu('')
      setRelanceContexte('')
      toast.success('Relance créée')
      fetchSinistre()
    } catch (error) {
      toast.error('Erreur lors de la création')
    } finally {
      setSendingRelance(false)
    }
  }

  async function handleGenerateRelanceIA() {
    setGeneratingRelance(true)
    try {
      const res = await fetch(`${API_URL}/api/ia/generer-relance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sinistre_id: id,
          destinataire_type: relanceType,
          contexte: relanceContexte,
        })
      })
      const data = await res.json()
      if (data.success) {
        setRelanceSujet(data.objet || '')
        setRelanceContenu(data.corps || '')
        toast.success('Relance IA générée')
      } else {
        toast.error(data.erreur || 'Erreur de génération')
      }
    } catch (err) {
      toast.error('Erreur de connexion au service IA')
    } finally {
      setGeneratingRelance(false)
    }
  }

  async function handleOuvertureAssureur() {
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${id}/ouverture-assureur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: profile.id })
      })
      if (!res.ok) throw new Error('Erreur')
      toast.success('Dossier ouvert auprès de l\'assureur')
      fetchSinistre()
    } catch (err) {
      toast.error('Erreur lors de l\'ouverture')
    } finally {
      setSaving(false)
    }
  }

  async function handleEnregistrerPaiement() {
    if (!paiementForm.montant) {
      toast.error('Veuillez saisir un montant')
      return
    }
    setSavingPaiement(true)
    try {
      // Update montant_rembourse directly via backend PATCH
      const montant = parseFloat(paiementForm.montant)
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ montant_rembourse: montant })
      })
      if (!res.ok) throw new Error('Erreur')

      // Create payment event
      const estimation = sinistre.montant_dommages_estime || 0
      const ecart = estimation ? Math.round(((montant - estimation) / estimation) * 100) : 0
      
      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'changement_statut',
        titre: 'Paiement recu',
        contenu: `Paiement de ${montant.toLocaleString('fr-FR')} EUR recu${paiementForm.reference ? ` (ref: ${paiementForm.reference})` : ''}${estimation ? `. Ecart vs estimation: ${ecart > 0 ? '+' : ''}${ecart}%` : ''}${paiementForm.commentaire ? `. ${paiementForm.commentaire}` : ''}`,
        nouvelle_valeur: { montant_paye: montant, reference: paiementForm.reference, ecart },
        source: 'manuel',
      })

      toast.success(`Paiement de ${formatCurrency(montant)} enregistré`)
      setShowPaiementModal(false)
      setPaiementForm({ montant: '', date: '', reference: '', commentaire: '' })
      fetchSinistre()
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement')
    } finally {
      setSavingPaiement(false)
    }
  }

  async function handleCloturerDossier() {
    setSavingCloture(true)
    try {
      // Use PATCH which works with existing statuses
      const res = await fetch(`${API_URL}/api/sinistres/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          statut: 'cloture',
          date_cloture: new Date().toISOString()
        })
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Erreur')

      const estimation = sinistre.montant_dommages_estime || 0
      const rembourse = sinistre.montant_rembourse || 0

      await createEvent(id, {
        tenant_id: sinistre.tenant_id,
        user_id: profile.id,
        type_evenement: 'changement_statut',
        titre: 'Dossier cloture',
        contenu: `Dossier cloture. Estimation: ${formatCurrency(estimation)} | Rembourse: ${formatCurrency(rembourse)}${clotureCommentaire ? `. ${clotureCommentaire}` : ''}`,
        nouvelle_valeur: { statut: 'cloture', estimation, rembourse },
        source: 'manuel',
      })

      toast.success('Dossier clôturé')
      setShowClotureModal(false)
      setClotureCommentaire('')
      fetchSinistre()
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la clôture')
    } finally {
      setSavingCloture(false)
    }
  }

  async function handleUpdateRelanceStatut(relanceId, newStatut) {
    try {
      await fetch(`${API_URL}/api/sinistres/relances/${relanceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut: newStatut,
          date_reponse: newStatut === 'repondue' ? new Date().toISOString() : null,
        }),
      })

      toast.success('Statut relance mis a jour')
      fetchSinistre()
    } catch (error) {
      toast.error('Erreur lors de la mise a jour')
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '--'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  function formatCurrency(amount) {
    if (amount == null) return '—'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Chargement du dossier...</p>
        </div>
      </div>
    )
  }

  if (!sinistre) {
    return (
      <div className="text-center py-20">
        <AlertCircle size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">Dossier introuvable</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-indigo-600 hover:underline">
          Retour aux sinistres
        </button>
      </div>
    )
  }

  const statutConfig = STATUT_OPTIONS.find(s => s.value === sinistre.statut) || STATUT_OPTIONS[0]
  const graviteConfig = GRAVITE_CONFIG[sinistre.gravite] || GRAVITE_CONFIG.faible
  const isOpen = !sinistre.date_cloture
  const isClosed = sinistre.statut === 'cloture' || sinistre.statut?.startsWith('cloture_') || sinistre.statut === 'sans_suite'
  const isAmiable = sinistre.mode_gestion === 'amiable' || (sinistre.est_amiable && !sinistre.mode_gestion)
  const isFlotte = sinistre.type_sinistre === 'flotte' || sinistre.polices?.type_police === 'flotte'

  // Determine current pipeline stage index (supports legacy statuts via mapping)
  const currentStageIdx = PIPELINE_STAGES.findIndex(s => s.key === sinistre.statut)
  const legacyIdx = LEGACY_STAGE_MAP[sinistre.statut]
  const pipelineIdx = currentStageIdx >= 0 ? currentStageIdx : (legacyIdx != null ? legacyIdx : (isClosed ? PIPELINE_STAGES.length - 1 : 0))

  // Champs requis non remplis — surbrillance rouge
  const champsManquants = []
  if (!sinistre.type_sinistre) champsManquants.push('Type de sinistre')
  if (!sinistre.date_fait) champsManquants.push('Date du fait')
  if (!sinistre.lieu && !sinistre.lieu_sinistre?.adresse) champsManquants.push('Lieu')
  if (!sinistre.mode_gestion) champsManquants.push('Mode de gestion')
  if (isFlotte && !isAmiable && !sinistre.responsabilite) champsManquants.push('Responsabilite')
  if (!sinistre.description) champsManquants.push('Description')
  const hasMissing = champsManquants.length > 0 && !isClosed
  const missingSet = new Set(champsManquants)
  const isDab = sinistre.type_sinistre === 'dab' || sinistre.polices?.type_police === 'dab'
  const differeEnCours = isDab && ['en_attente_facture', 'facture_transmise'].includes(sinistre.indemnite_differee_statut)

  // Bloc Contacts — colonne latérale (assurance) ou sous Informations du sinistre (amiable flotte)
  const contactsBlock = (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="contacts-block">
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
        <h2 className="font-semibold text-slate-800 text-sm">Contacts</h2>
      </div>
      <div className="p-5 space-y-4">
        {sinistre.declarant && (
          <ContactRow
            role="Déclarant"
            name={`${sinistre.declarant.prenom} ${sinistre.declarant.nom}`}
            email={sinistre.declarant.email}
            phone={sinistre.declarant.telephone}
          />
        )}
        {sinistre.polices?.compagnies && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-1">Compagnie d'assurance</p>
            <p className="text-sm font-medium text-slate-800">{sinistre.polices.compagnies.nom}</p>
          </div>
        )}
        <div className="pt-3 border-t border-slate-100">
          <ContactsDossier
            sinistreId={id}
            contacts={sinistre.contacts_dossier || []}
            onRefresh={fetchSinistre}
          />
        </div>
      </div>
    </div>
  )

  // Bloc Evaluation financiere (provision compagnie, mode de gestion) — sidebar ou, en flotte, dans la colonne principale
  const financeBlock = (
          <div className={isFlotte ? '' : `bg-white rounded-xl border shadow-sm overflow-hidden ${missingSet.has('Provision compagnie') ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'}`} data-testid="finance-block">
            {!isFlotte && (
            <div className={`px-5 py-4 border-b ${missingSet.has('Provision compagnie') ? 'border-red-100 bg-red-50' : 'border-slate-100 bg-slate-50'}`}>
              <h2 className="font-semibold text-slate-800 text-sm">Évaluation financière</h2>
            </div>
            )}
            <div className={isFlotte ? 'space-y-4' : 'p-5 space-y-4'}>
              {/* Mode de gestion V2 — masque en amiable (deja dans AmiableSinistreView) */}
              {!isAmiable && <ModeGestionBlock sinistre={sinistre} onRefresh={fetchSinistre} />}

              {/* Bloc Reglement Amiable — masque en amiable (deja dans AmiableSinistreView) */}
              {!isAmiable && <ReglementAmiableBlock sinistre={sinistre} onRefresh={fetchSinistre} />}

              {/* ══ PROVISION COMPAGNIE — SOURCE UNIQUE DE VÉRITÉ — masque pour amiable ══ */}
              {!isAmiable && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 -mx-1" data-testid="provision-compagnie-block">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Provision compagnie</span>
                    {sinistre.provision_source && (
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        sinistre.provision_source === 'releve_compagnie'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {sinistre.provision_source === 'releve_compagnie' ? 'Releve compagnie' : 'Gestionnaire'}
                      </span>
                    )}
                  </div>
                  {editMode === 'provision' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-32 h-10 px-3 text-base text-right font-mono font-bold border-2 border-amber-400 rounded-lg bg-white focus:ring-2 focus:ring-amber-500"
                        autoFocus
                        data-testid="provision-input"
                      />
                      <button onClick={handleUpdateProvision} disabled={saving} className="text-green-600 hover:text-green-700 p-1">
                        <Save size={18} />
                      </button>
                      <button onClick={() => setEditMode(null)} className="text-slate-400 hover:text-slate-600 p-1">
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditMode('provision'); setEditValue(sinistre.provision_compagnie || '') }}
                      className="font-mono text-lg font-bold text-amber-900 flex items-center gap-2 hover:text-amber-700 transition-colors"
                      data-testid="edit-provision-btn"
                    >
                      {formatCurrency(sinistre.provision_compagnie)}
                      <Edit3 size={14} className="opacity-50" />
                    </button>
                  )}
                </div>
              </div>
              )}
              
              {!isAmiable && sinistre.montant_reclamation && (
                <FinancialRow label="Réclamation" value={formatCurrency(sinistre.montant_reclamation)} />
              )}
              {!isAmiable && sinistre.montant_offre_compagnie && (
                <FinancialRow label="Offre compagnie" value={formatCurrency(sinistre.montant_offre_compagnie)} />
              )}
              {!isAmiable && !isDab && sinistre.montant_rembourse && (
                <FinancialRow label="Remboursé" value={formatCurrency(sinistre.montant_rembourse)} success />
              )}
              {!isAmiable && isDab && (
                <div className="flex justify-between items-center" data-testid="indemnite-immediate-row">
                  <span className="text-sm text-slate-500">Indemnité immédiate</span>
                  {editMode === 'immediate' ? (
                    <div className="flex items-center gap-1.5">
                      <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus data-testid="indemnite-immediate-input"
                        className="w-28 h-8 px-2 text-sm text-right font-mono border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-400 outline-none" />
                      <button onClick={handleUpdateIndemniteImmediate} disabled={saving} className="text-green-600 hover:text-green-700 p-1" data-testid="indemnite-immediate-save"><Save size={15} /></button>
                      <button onClick={() => setEditMode(null)} className="text-slate-400 hover:text-slate-600 p-1"><X size={15} /></button>
                    </div>
                  ) : (
                    <button onClick={() => { if (!isClosed) { setEditMode('immediate'); setEditValue(sinistre.montant_rembourse ?? '') } }}
                      className={`font-mono text-sm font-semibold flex items-center gap-1.5 ${sinistre.montant_rembourse ? 'text-emerald-600' : 'text-slate-400'} ${isClosed ? '' : 'hover:text-emerald-700'}`}
                      data-testid="edit-indemnite-immediate-btn">
                      {sinistre.montant_rembourse ? formatCurrency(sinistre.montant_rembourse) : 'À renseigner'}
                      {!isClosed && <Edit3 size={12} className="opacity-50" />}
                    </button>
                  )}
                </div>
              )}
              {!isAmiable && isDab && (
                <IndemniteDiffereeBlock
                  sinistre={sinistre}
                  onRefresh={fetchSinistre}
                  readOnly={isClosed}
                  onEvent={(ev) => createEvent(id, { tenant_id: sinistre.tenant_id, user_id: profile.id, source: 'manuel', ...ev })}
                />
              )}
              {!isAmiable && sinistre.economie_amiable && (
                <FinancialRow label="Économie amiable" value={formatCurrency(sinistre.economie_amiable)} success />
              )}
              {!isAmiable && sinistre.cout_amiable_reel && (
                <FinancialRow label="Coût amiable réel" value={formatCurrency(sinistre.cout_amiable_reel)} />
              )}
              {!isAmiable && sinistre.forfait_irsa && (
                <FinancialRow label="Forfait IRSA" value={formatCurrency(sinistre.forfait_irsa)} />
              )}
            </div>
          </div>
  )

  // Onglets Historique / Documents / Relances / Contrat / Memoire IA
  const tabsBlock = (
    <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-200">
              {[
                { key: 'timeline', label: 'Historique', icon: Clock, count: events.length },
                { key: 'documents', label: 'Documents', icon: FileText, count: documents.length },
                { key: 'relances', label: 'Relances', icon: Bell, count: relances.length },
                { key: 'contrat', label: 'Contexte contrat', icon: Shield, count: 0 },
                { key: 'memoire', label: 'Mémoire IA', icon: Brain, count: 0 },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`c2-tab-${tab.key}`}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2
                    ${activeTab === tab.key 
                      ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  <tab.icon size={15} />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.key ? 'bg-indigo-100' : 'bg-slate-100'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-5">
              {activeTab === 'timeline' && <SinistreTimeline events={events} onRefresh={fetchSinistre} />}
              {activeTab === 'documents' && (
                <SinistreDocuments 
                  documents={documents} 
                  sinistreId={id}
                  tenantId={sinistre.tenant_id}
                  onRefresh={fetchSinistre}
                  canUpload={true}
                  userRole={profile?.role || 'c2_gestionnaire'}
                />
              )}
              {activeTab === 'relances' && (
                <RelancesList 
                  relances={relances}
                  onUpdateStatut={(rId, status) => {
                    if (rId && status) handleUpdateRelanceStatut(rId, status)
                    else fetchSinistre() // refresh after create
                  }}
                  sinistreId={id}
                  tenantId={sinistre.tenant_id}
                  sinistre={sinistre}
                />
              )}
              {activeTab === 'contrat' && (
                <ContratContextBlock sinistreId={id} />
              )}
              {activeTab === 'memoire' && (
                <C1MemoirePanel tenantId={sinistre.tenant_id} />
              )}
            </div>
          </div>

          {/* Notes */}
          {isOpen && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Ajouter une note
              </label>
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <RichNoteEditor value={noteInterne} onChange={setNoteInterne} placeholder="Saisir une note..." testId="note-interne-input" />
                  {/* Color selector for new note */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 mr-1">Couleur :</span>
                    {[
                      { key: '', dot: 'bg-slate-300' },
                      { key: 'jaune', dot: 'bg-yellow-400' },
                      { key: 'orange', dot: 'bg-orange-400' },
                      { key: 'rose', dot: 'bg-pink-400' },
                      { key: 'rouge', dot: 'bg-red-400' },
                      { key: 'violet', dot: 'bg-purple-400' },
                      { key: 'bleu', dot: 'bg-blue-400' },
                      { key: 'vert', dot: 'bg-emerald-400' },
                      { key: 'gris', dot: 'bg-slate-400' },
                    ].map(c => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setNoteCouleur(c.key)}
                        className={`w-5 h-5 rounded-full ${c.dot} transition-transform hover:scale-110 ${
                          noteCouleur === c.key ? 'ring-2 ring-offset-1 ring-indigo-400' : ''
                        }`}
                        data-testid={`new-note-color-${c.key || 'default'}`}
                      />
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleAddNoteInterne}
                  disabled={!noteToPlainText(noteInterne).trim() || sendingNote}
                  data-testid="send-note-btn"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium
                    hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                    flex items-center gap-2 self-end"
                >
                  <Send size={14} />
                  Ajouter
                </button>
              </div>
            </div>
          )}
    </>
  )



  return (
    <div className="p-8 pb-12" data-testid="c2-sinistre-detail">
      <ConflitEditionModal conflit={conflit} onFermer={fermerConflit} onRecharger={() => { fermerConflit(); brouillon.discard() }} />
      <BrouillonBar count={brouillon.count} saving={brouillon.flushing} onSave={() => brouillon.flush()} onDiscard={brouillon.discard} />
      {autresPresents.length > 0 && <div className="mb-4"><PresenceBanner autres={autresPresents} /></div>}
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <button
          onClick={async () => { await brouillon.flush({ refresh: false }); if (window.history.length > 1) navigate(-1); else navigate('/c2/clients') }}
          className="mt-1 w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {(sinistre.mode_gestion === 'amiable' || sinistre.est_amiable)
                ? (sinistre.numero_sinistre_client || sinistre.numero_sinistre)
                : (sinistre.numero_dossier_compagnie || sinistre.numero_sinistre)}
            </h1>
            {/* Sub-reference */}
            <span className="text-xs text-slate-400 font-mono">{sinistre.numero_sinistre}</span>
            
            {/* Editable status badge */}
            {editMode === 'statut' ? (
              <div className="flex items-center gap-2">
                <select
                  value={sinistre.statut}
                  onChange={(e) => handleUpdateStatut(e.target.value)}
                  disabled={saving}
                  className="h-8 px-2 text-xs border border-slate-300 rounded-md bg-white"
                >
                  {STATUT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button onClick={() => setEditMode(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditMode('statut')}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statutConfig.color} hover:ring-2 hover:ring-slate-300 transition-all`}
                data-testid="edit-statut-btn"
              >
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statutConfig.dot}`} />
                {statutConfig.label}
                <Edit3 size={10} className="ml-1.5 opacity-50" />
              </button>
            )}
            
            {sinistre.est_amiable && (
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                Amiable
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-slate-500">
              {sinistre.nature_sinistre || TYPE_SINISTRE_LABELS[sinistre.type_sinistre]}
            </p>
            <span className="text-slate-300">|</span>
            <p className="text-sm text-slate-500">{sinistre.tenants?.nom}</p>
            {sinistre.polices?.numero_police && (
              <>
                <span className="text-slate-300">|</span>
                <p className="text-sm text-slate-500">Police {sinistre.polices.numero_police}</p>
              </>
            )}
          </div>
        </div>

        {/* Delete button — admins uniquement */}
        {['master_admin', 'c2_admin'].includes(profile?.role) ? (
          <button
            onClick={handleDeleteSinistre}
            className="mt-1 w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors shrink-0"
            title="Supprimer ce sinistre (administrateur)"
            data-testid="delete-sinistre-btn"
          >
            <Trash2 size={16} className="text-red-500" />
          </button>
        ) : (
          <span
            className="mt-1 w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 cursor-not-allowed"
            title="Suppression reservee aux administrateurs C2 — demandez la validation d'un superviseur"
            data-testid="delete-sinistre-locked"
          >
            <Trash2 size={16} className="text-slate-300" />
          </span>
        )}
      </div>

      <InformationsImportantesBlock sinistre={sinistre} onRefresh={fetchSinistre} readOnly={isClosed} />

      {/* ── PIPELINE VISUEL ── */}
      {/* AMIABLE VIEW — blocs dédiés amiable (pipeline, infos reglement, avancement, IRSA/IRCA) */}
      {isAmiable && (
        <AmiableSinistreView sinistre={sinistre} onRefresh={fetchSinistre} contactsSlot={contactsBlock} />
      )}

      {/* STANDARD VIEW — pipeline standard + actions (masqué si amiable) */}
      {!isAmiable && (
      <>
      {/* Pipeline standard — bandeau des étapes masqué en flotte (statut via le badge d'en-tête) */}
      <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6 ${isFlotte ? 'hidden' : ''}`} data-testid="sinistre-pipeline">
        {!isFlotte && (
        <div className="flex items-center justify-between gap-1" data-testid="pipeline-stages">
          {PIPELINE_STAGES.map((stage, idx) => {
            const isPast = idx < pipelineIdx
            const isCurrent = idx === pipelineIdx
            const isClosedStage = isClosed && idx === PIPELINE_STAGES.length - 1
            const StageIcon = stage.icon
            return (
              <React.Fragment key={stage.key}>
                {idx > 0 && (
                  <div className={`flex-1 h-0.5 mx-1 rounded ${isPast || isCurrent ? 'bg-indigo-400' : 'bg-slate-200'}`} />
                )}
                <button
                  onClick={() => !isClosed && handleUpdateStatut(stage.key)}
                  disabled={isClosed}
                  className={`flex flex-col items-center gap-1 min-w-[80px] group transition-all ${isClosed && !isClosedStage ? 'opacity-40' : ''}`}
                  title={`Passer à: ${stage.label}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all
                    ${isCurrent ? 'bg-indigo-600 text-white ring-4 ring-indigo-100 scale-110' : 
                      isPast ? 'bg-indigo-100 text-indigo-600' : 
                      'bg-slate-100 text-slate-400 group-hover:bg-slate-200'}`}>
                    <StageIcon size={16} />
                  </div>
                  <span className={`text-[10px] font-medium text-center leading-tight
                    ${isCurrent ? 'text-indigo-700' : isPast ? 'text-indigo-500' : 'text-slate-400'}`}>
                    {stage.label}
                  </span>
                </button>
              </React.Fragment>
            )
          })}
        </div>
        )}

        {/* Actions contextuelles sous le pipeline */}
        {isOpen && (
          <div className={`flex items-center gap-2 flex-wrap ${isFlotte ? '' : 'mt-4 pt-3 border-t border-slate-100'}`}>
            {sinistre.statut === 'declare_c2' && (
              <>
                <button
                  onClick={() => handleUpdateStatut('attente_elements')}
                  disabled={saving}
                  data-testid="action-attente-elements"
                  className="px-4 py-2 bg-yellow-50 text-yellow-800 border border-yellow-300 rounded-lg text-sm font-medium hover:bg-yellow-100 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  <Clock size={14} />
                  Attente éléments
                </button>
                <button
                  onClick={() => handleUpdateStatut('attente_ouverture_compagnie')}
                  disabled={saving}
                  data-testid="action-attente-ouverture"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  <Send size={14} />
                  Ouverture compagnie
                </button>
              </>
            )}
            {sinistre.statut === 'attente_elements' && (
              <button
                onClick={() => handleUpdateStatut('attente_ouverture_compagnie')}
                disabled={saving}
                data-testid="action-attente-ouverture"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                <Send size={14} />
                Dossier complet — Ouverture compagnie
              </button>
            )}
            {['attente_ouverture_compagnie', 'ouvert_compagnie', 'attente_compagnie_client', 'expertise_ou_sans'].includes(sinistre.statut) && (
              <>
                <button
                  onClick={() => { setRelanceType('compagnie'); setShowRelanceModal(true) }}
                  data-testid="action-relance-assureur"
                  className="px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 flex items-center gap-2 transition-colors"
                >
                  <Bell size={14} />
                  Relancer assureur
                </button>
                <button
                  onClick={() => { setRelanceType('client'); setShowRelanceModal(true) }}
                  data-testid="action-relance-client"
                  className="px-3 py-2 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg text-sm font-medium hover:bg-cyan-100 flex items-center gap-2 transition-colors"
                >
                  <User size={14} />
                  Demander infos client
                </button>
              </>
            )}
            {['ouvert_compagnie', 'attente_compagnie_client', 'expertise_ou_sans', 'attente_reglement'].includes(sinistre.statut) && (
              <button
                onClick={() => setShowPaiementModal(true)}
                data-testid="action-paiement"
                className="px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-2 transition-colors"
              >
                <Euro size={14} />
                Enregistrer paiement
              </button>
            )}
            {sinistre.statut !== 'declare_c2' && !isClosed && (
              <button
                onClick={() => setShowClotureModal(true)}
                disabled={differeEnCours}
                title={differeEnCours ? 'Clôture impossible : indemnité différée en attente' : undefined}
                data-testid="action-cloturer"
                className="px-3 py-2 bg-slate-100 text-slate-600 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-100 flex items-center gap-2 transition-colors ml-auto"
              >
                <Lock size={14} />
                Clôturer
              </button>
            )}
          </div>
        )}
      </div>
      </>
      )}

      {/* === BLOCS PARTAGES (visibles en standard ET amiable) === */}

      {/* Alerte champs manquants */}
      {hasMissing && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl mb-4" data-testid="missing-fields-alert">
          <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Dossier incomplet — {champsManquants.length} champ(s) manquant(s)</p>
            <p className="text-xs text-red-600 mt-0.5">{champsManquants.join(' · ')}</p>
          </div>
        </div>
      )}

      {/* Main grid — en amiable (flotte) : pas de colonne latérale, historique pleine largeur */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main content */}
        <div className={`${isAmiable ? 'lg:col-span-3' : 'lg:col-span-2'} space-y-4`}>
          {/* Fleet-specific blocks (only for flotte sinistres) */}
          <FlotteSinistreBlocks sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} isAmiable={isAmiable} financeSlot={isFlotte && !isAmiable ? financeBlock : null} />

          {/* Transport-specific blocks (polices rc_transport / ad_valorem) */}
          <TransportSinistreBlocks sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} userId={profile?.id} />

          {/* Info card (standard) — masqué en mode flotte (déjà affiché dans FlotteSinistreBlocks) */}
          {!isFlotte && (
          <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
            (missingSet.has('Type de sinistre') || missingSet.has('Date du fait') || missingSet.has('Lieu') || missingSet.has('N. dossier compagnie'))
              ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'
          }`}>
            <div className={`px-5 py-4 border-b flex items-center justify-between ${
              (missingSet.has('Type de sinistre') || missingSet.has('Date du fait'))
                ? 'border-red-100 bg-red-50' : 'border-slate-100 bg-slate-50'
            }`}>
              <h2 className="font-semibold text-slate-800 text-sm">Informations du sinistre</h2>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${graviteConfig.color}`}>
                {graviteConfig.label}
              </span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {/* 1. N° dossier compagnie / client */}
                <EditableNumeroDossier sinistreId={id} sinistre={sinistre} onRefresh={fetchSinistre} />
                {/* 2. Date reception declaration */}
                <EditableDateRow icon={<Calendar size={14} />} label="Recu le" field="date_reception_declaration" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                {/* 3. Canal de reception */}
                <EditableTextField icon={<FileText size={14} />} label="Canal" field="canal_reception_amiable" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                {/* 4. Date du sinistre */}
                <EditableDateRow icon={<Calendar size={14} />} label="Date du sinistre" field="date_fait" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                {/* 5. Lieu */}
                <EditableTextField icon={<MapPin size={14} />} label="Lieu" field="lieu" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} colSpan />
                {/* 6. Description (modifiable) */}
                <EditableTextField icon={<FileText size={14} />} label="Description" field="description" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} colSpan />
                {/* 12. Site — selecteur pour non-flotte */}
                {!isFlotte && (
                  <SiteSelector sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                )}
                {isFlotte && sinistre.sites && (
                  <InfoRow 
                    icon={<MapPin size={14} className="text-emerald-500" />}
                    label="Site"
                    value={`${sinistre.sites.nom}${sinistre.sites.ville ? ` \u2014 ${sinistre.sites.ville}` : ''}`}
                  />
                )}
              </div>

              {/* Bloc Tiers — visible si données tiers ou amiable */}
              {(sinistre.tiers_nom || sinistre.tiers_email || sinistre.tiers_telephone || sinistre.mode_gestion === 'amiable' || sinistre.est_amiable) && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-3">Tiers</p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                    <EditableTextField icon={<User size={14} />} label="Nom prenom" field="tiers_nom" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                    <EditableTextField icon={<FileText size={14} />} label="Societe" field="tiers_assurance" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                    <EditableTextField icon={<FileText size={14} />} label="Telephone" field="tiers_telephone" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                    <EditableTextField icon={<FileText size={14} />} label="Email" field="tiers_email" sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />
                  </div>
                </div>
              )}

              {sinistre.tiers_impliques?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-2">Tiers implique(s)</p>
                  {sinistre.tiers_impliques.map((tiers, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <User size={14} className="text-slate-400" />
                      <span className="text-slate-700">{tiers.nom}</span>
                      {tiers.contact && <span className="text-slate-400">— {tiers.contact}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Tabs (historique, documents...) — en flotte assurance : pleine largeur sous la grille */}
          {!(isFlotte && !isAmiable) && tabsBlock}
        </div>

        {/* Right column - Sidebar (masquée en amiable : contacts déplacés dans AmiableSinistreView, résumé supprimé) */}
        {!isAmiable && (
        <div className="space-y-4">
          {/* Financial — masque en amiable (flotte) ; en flotte assurance : rendu dans le bloc Evaluation financiere (colonne principale) */}
          {!isAmiable && !isFlotte && financeBlock}

          {/* Eligibilite amiable — hors flotte (en flotte : dans le bloc Elements) */}
          {!isAmiable && !isFlotte && <EligibiliteAmiableBlock sinistre={sinistre} onRefresh={fetchSinistre} />}

          {/* Expertise — flotte assurance : a la place de l'ancien bloc Reglement amiable */}
          {isFlotte && !isAmiable && <ExpertiseBlock sinistre={sinistre} sinistreId={id} onRefresh={fetchSinistre} />}

          {/* Contacts */}
          {contactsBlock}

          {/* Quick stats — supprime en flotte */}
          {!isFlotte && (
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white">
            <p className="text-xs font-medium text-slate-400 mb-3">Résumé du dossier</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-300">Événements</span>
                <span className="font-mono font-bold">{events.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-300">Documents</span>
                <span className="font-mono font-bold">{documents.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-300">Relances</span>
                <span className="font-mono font-bold">{relances.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-300">Ancienneté</span>
                <span className="font-mono font-bold">
                  {Math.floor((new Date() - new Date(sinistre.date_declaration)) / (1000 * 60 * 60 * 24))}j
                </span>
              </div>
            </div>
          </div>
          )}
        </div>
        )}
      </div>

      {/* Historique pleine largeur — flotte assurance */}
      {isFlotte && !isAmiable && <div className="mt-6">{tabsBlock}</div>}

      {/* Section IA & Doctrine - Full width below main content */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Doctrine Recommendation - Takes 1 column */}
        <div className="lg:col-span-1">
          <DoctrineRecommandationBlock 
            sinistreId={id}
            onRecommandationUpdate={fetchSinistre}
          />
        </div>
        
        {/* AI Analysis - Takes 1 column */}
        <div className="lg:col-span-1">
          <AnalyseIABlock 
            sinistreId={id}
            tenantId={sinistre.tenant_id}
            onAnalyseComplete={fetchSinistre}
          />
        </div>
        
        {/* Declaration Draft - Takes 1 column */}
        <div className="lg:col-span-1">
          <DeclarationAssureurBlock 
            sinistreId={id}
            onBrouillonGenere={fetchSinistre}
          />
        </div>
      </div>

      {/* ── MODAL RELANCE (avec IA) ── */}
      {showRelanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">
                {relanceType === 'compagnie' ? 'Relance compagnie d\'assurance' : 'Message au client'}
              </h3>
              <button onClick={() => { setShowRelanceModal(false); setRelanceSujet(''); setRelanceContenu(''); setRelanceContexte('') }} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Destinataire</label>
                <div className="flex gap-3">
                  {[
                    { value: 'compagnie', label: 'Compagnie', icon: Building },
                    { value: 'client', label: 'Client', icon: User },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setRelanceType(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        relanceType === opt.value 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <opt.icon size={16} />
                      <span className="font-medium text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* IA Generation */}
              <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Brain size={16} className="text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-700">Génération IA</span>
                </div>
                <div className="mb-3">
                  <input
                    type="text"
                    value={relanceContexte}
                    onChange={(e) => setRelanceContexte(e.target.value)}
                    placeholder="Contexte optionnel (ex: pas de réponse depuis 3 semaines, pièces manquantes...)"
                    className="w-full h-9 px-3 text-sm border border-indigo-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  />
                </div>
                <button
                  onClick={handleGenerateRelanceIA}
                  disabled={generatingRelance}
                  data-testid="generate-relance-ia-btn"
                  className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                >
                  {generatingRelance ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Brain size={14} />
                      Générer avec l'IA
                    </>
                  )}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Objet du message</label>
                <input
                  type="text"
                  value={relanceSujet}
                  onChange={(e) => setRelanceSujet(e.target.value)}
                  placeholder="Ex: Relance - Sinistre SIN-... - Contrat n°..."
                  className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-700">Contenu du message</label>
                  {relanceContenu && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(relanceContenu); toast.success('Copié !') }}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                      data-testid="copy-relance-btn"
                    >
                      <FileText size={12} />
                      Copier le texte
                    </button>
                  )}
                </div>
                <textarea
                  value={relanceContenu}
                  onChange={(e) => setRelanceContenu(e.target.value)}
                  rows={8}
                  placeholder="Contenu de la relance... Utilisez la génération IA ci-dessus ou rédigez manuellement."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-between">
              <button
                onClick={() => { navigator.clipboard.writeText(`Objet: ${relanceSujet}\n\n${relanceContenu}`); toast.success('Message complet copié !') }}
                disabled={!relanceContenu}
                className="px-4 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FileText size={14} />
                Copier tout
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowRelanceModal(false); setRelanceSujet(''); setRelanceContenu(''); setRelanceContexte('') }}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateRelance}
                  disabled={sendingRelance || !relanceSujet.trim() || !relanceContenu.trim()}
                  data-testid="save-relance-btn"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sendingRelance ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  Enregistrer la relance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PAIEMENT ── */}
      {showPaiementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Euro size={18} className="text-emerald-600" />
                Enregistrer un paiement
              </h3>
              <button onClick={() => setShowPaiementModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {sinistre.montant_dommages_estime && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <span className="text-slate-500">Estimation initiale :</span>
                  <span className="ml-2 font-semibold text-slate-800">{formatCurrency(sinistre.montant_dommages_estime)}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Montant reçu *</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={paiementForm.montant}
                    onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                    placeholder="0.00"
                    data-testid="paiement-montant-input"
                    className="w-full h-10 pl-3 pr-10 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">EUR</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Date du paiement</label>
                <input
                  type="date"
                  value={paiementForm.date}
                  onChange={(e) => setPaiementForm({ ...paiementForm, date: e.target.value })}
                  className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Référence de paiement</label>
                <input
                  type="text"
                  value={paiementForm.reference}
                  onChange={(e) => setPaiementForm({ ...paiementForm, reference: e.target.value })}
                  placeholder="Ex: REF-2026-001"
                  className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Commentaire</label>
                <textarea
                  value={paiementForm.commentaire}
                  onChange={(e) => setPaiementForm({ ...paiementForm, commentaire: e.target.value })}
                  rows={2}
                  placeholder="Notes sur le paiement..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowPaiementModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                Annuler
              </button>
              <button
                onClick={handleEnregistrerPaiement}
                disabled={savingPaiement || !paiementForm.montant}
                data-testid="confirm-paiement-btn"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                {savingPaiement ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle size={14} />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CLOTURE ── */}
      {showClotureModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Lock size={18} className="text-slate-600" />
                Clôturer le dossier
              </h3>
              <button onClick={() => setShowClotureModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                Cette action est définitive. Le dossier sera archivé et ne pourra plus être modifié.
              </div>
              {sinistre.montant_dommages_estime && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500 mb-1">Estimation</div>
                    <div className="font-semibold text-slate-800">{formatCurrency(sinistre.montant_dommages_estime)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500 mb-1">Remboursé</div>
                    <div className="font-semibold text-emerald-700">{formatCurrency(sinistre.montant_rembourse || 0)}</div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Commentaire de clôture</label>
                <textarea
                  value={clotureCommentaire}
                  onChange={(e) => setClotureCommentaire(e.target.value)}
                  rows={3}
                  placeholder="Motif de clôture, résumé final..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-slate-500 resize-none"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowClotureModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                Annuler
              </button>
              <button
                onClick={handleCloturerDossier}
                disabled={savingCloture}
                data-testid="confirm-cloture-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {savingCloture ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Lock size={14} />
                )}
                Clôturer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function SiteSelector({ sinistre, sinistreId, onRefresh }) {
  const [sites, setSites] = useState([])
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!sinistre?.tenant_id) return
    fetch(`${API_URL}/api/sites?tenant_id=${sinistre.tenant_id}&statut=actif`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setSites(d?.sites || []))
      .catch(() => {})
  }, [sinistre?.tenant_id])

  async function handleChange(newSiteId) {
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: newSiteId || null }),
      })
      if (r.ok) {
        toast.success('Site mis a jour')
        setEditing(false)
        onRefresh?.()
      }
    } catch { toast.error('Erreur') }
    finally { setSaving(false) }
  }

  const currentSite = sinistre.sites
  const currentName = currentSite ? `${currentSite.nom}${currentSite.ville ? ` \u2014 ${currentSite.ville}` : ''}` : null

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 py-1.5 pl-6 col-span-2" data-testid="site-selector-edit">
        <MapPin size={14} className="text-emerald-500 shrink-0" />
        <div className="flex-1">
          <label className="text-[11px] text-slate-500 mb-0.5 block">Site</label>
          <select
            value={sinistre.site_id || ''}
            onChange={e => handleChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
            autoFocus
            className="w-full px-2 py-1.5 text-sm border border-indigo-300 rounded-lg focus:ring-1 focus:ring-indigo-400"
            data-testid="site-selector-dropdown"
          >
            <option value="">— Aucun site —</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>{s.nom}{s.ville ? ` — ${s.ville}` : ''}</option>
            ))}
          </select>
        </div>
        {saving && <Loader2 size={12} className="animate-spin text-indigo-500" />}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 py-1.5 pl-6 group cursor-pointer col-span-2" onClick={() => setEditing(true)} data-testid="site-selector-display">
      <MapPin size={14} className="text-emerald-500 shrink-0" />
      <div>
        <div className="text-[11px] text-slate-500">Site</div>
        <div className="text-sm text-slate-800 font-medium flex items-center gap-1 group-hover:text-indigo-600">
          {currentName || <span className="text-slate-300 italic">Selectionner un site</span>}
          <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value, colSpan }) {
  const isEmpty = !value || value === 'undefined' || value === 'null (null)'
  return (
    <div className={colSpan ? 'col-span-2' : ''}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        {icon}
        {label}
      </div>
      {isEmpty ? (
        <p className="text-sm text-red-400 font-medium italic">Non renseigne</p>
      ) : (
        <p className="text-sm text-slate-800 font-medium">{value}</p>
      )}
    </div>
  )
}

function FinancialRow({ label, value, success }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`font-mono text-sm font-semibold ${success ? 'text-emerald-600' : 'text-slate-700'}`}>
        {value}
      </span>
    </div>
  )
}

function ContactRow({ role, name, email, phone }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
        {name.split(' ').map(n => n[0]).join('')}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400">{role}</p>
        <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
        <p className="text-xs text-slate-500 truncate">{email}</p>
        {phone && <p className="text-xs text-slate-500">{phone}</p>}
      </div>
    </div>
  )
}

function RelanceCard({ r, cfg, isOverdue, onUpdateStatut }) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    const text = `Objet: ${r.sujet || ''}\nA: ${r.destinataire_nom || ''} ${r.destinataire_email ? `<${r.destinataire_email}>` : ''}\n\n${r.contenu || ''}`
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      className={`rounded-lg border transition-all ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'} ${expanded ? 'ring-1 ring-indigo-300' : ''}`}
      data-testid={`relance-${r.id}`}
    >
      {/* Header — always visible, clickable */}
      <div
        className="p-4 cursor-pointer hover:bg-white/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                r.destinataire_type === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
              }`}>
                {r.destinataire_type === 'client' ? 'Client' : 'Compagnie'}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cfg.color}`}>
                {cfg.label}
              </span>
              {isOverdue && (
                <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
                  En retard
                </span>
              )}
              {r.prochaine_relance && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Calendar size={10} />
                  {new Date(r.prochaine_relance).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-slate-800">{r.sujet}</p>
            {r.destinataire_nom && (
              <p className="text-xs text-slate-500 mt-0.5">A: {r.destinataire_nom} {r.destinataire_email ? `<${r.destinataire_email}>` : ''}</p>
            )}
            {!expanded && r.contenu && r.contenu !== 'A rediger' && (
              <p className="text-xs text-slate-600 mt-1 line-clamp-2">{r.contenu}</p>
            )}
          </div>
          <ChevronDown size={16} className={`text-slate-400 transition-transform shrink-0 mt-1 ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded content — full email + copy button */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {r.contenu && r.contenu !== 'A rediger' ? (
            <div className="bg-white border border-slate-200 rounded-lg p-3 relative">
              <button
                onClick={handleCopy}
                className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 transition-colors"
                data-testid="copy-relance-btn"
              >
                {copied ? (
                  <><CheckCircle size={11} className="text-green-500" /> Copie !</>
                ) : (
                  <><Copy size={11} /> Copier</>
                )}
              </button>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed pr-16">{r.contenu}</pre>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Contenu non redige</p>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <p className="text-xs text-slate-400">
              Creee le {new Date(r.created_at).toLocaleDateString('fr-FR')}
              {r.date_reponse && ` | Repondue le ${new Date(r.date_reponse).toLocaleDateString('fr-FR')}`}
            </p>
            <div className="flex items-center gap-2">
              {r.statut === 'a_envoyer' && (
                <button
                  onClick={() => onUpdateStatut(r.id, 'envoyee')}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  data-testid={`relance-mark-sent-${r.id}`}
                >
                  Marquer envoyee
                </button>
              )}
              {(r.statut === 'envoyee' || r.statut === 'a_envoyer') && (
                <button
                  onClick={() => onUpdateStatut(r.id, 'repondue')}
                  className="text-xs text-green-600 hover:text-green-700 font-medium"
                  data-testid={`relance-mark-replied-${r.id}`}
                >
                  Marquer repondue
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function RelancesList({ relances, onUpdateStatut, sinistreId, tenantId, sinistre }) {
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draftingAI, setDraftingAI] = useState(false)
  const [form, setForm] = useState({
    destinataire_type: 'compagnie',
    destinataire_nom: sinistre?.polices?.compagnies?.nom || '',
    destinataire_email: '',
    prochaine_relance: '',
    sujet: '',
    contenu: '',
  })

  function resetForm() {
    setForm({
      destinataire_type: 'compagnie',
      destinataire_nom: sinistre?.polices?.compagnies?.nom || '',
      destinataire_email: '',
      prochaine_relance: '',
      sujet: '',
      contenu: '',
    })
  }

  async function handleDraftAI() {
    setDraftingAI(true)
    try {
      const res = await fetch(`${API_URL}/api/relances/draft-ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sinistre_id: sinistreId,
          destinataire_type: form.destinataire_type,
        }),
      })
      const data = await res.json()
      if (data.success) {
        setForm(prev => ({ ...prev, sujet: data.sujet, contenu: data.contenu }))
        toast.success('Brouillon IA genere')
      } else {
        toast.error(data.detail || 'Erreur IA')
      }
    } catch { toast.error('Erreur IA') }
    finally { setDraftingAI(false) }
  }

  async function handleSave(e) {
    e.preventDefault()
    if (!form.prochaine_relance || !form.destinataire_nom) {
      toast.error('Remplissez la date et le destinataire')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/relances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sinistre_id: sinistreId,
          tenant_id: tenantId,
          ...form,
          sujet: form.sujet || 'Relance',
          contenu: form.contenu || 'A rediger',
        }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Relance planifiee')
        setShowForm(false)
        resetForm()
        onUpdateStatut(null, null) // trigger refresh
      } else {
        toast.error(data.detail || 'Erreur')
      }
    } catch { toast.error('Erreur') }
    finally { setSaving(false) }
  }

  const STATUT_CONFIG = {
    a_envoyer: { label: 'Planifiee', color: 'bg-amber-100 text-amber-700' },
    envoyee: { label: 'Envoyee', color: 'bg-blue-100 text-blue-700' },
    en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
    repondue: { label: 'Repondue', color: 'bg-green-100 text-green-700' },
  }

  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="space-y-3" data-testid="relances-list">
      {/* Add relance button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 hover:border-indigo-300 hover:text-indigo-500 transition-colors text-sm"
          data-testid="add-relance-btn"
        >
          <Plus size={16} /> Planifier une relance
        </button>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSave} className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-3" data-testid="relance-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-indigo-800">Nouvelle relance</h3>
            <button type="button" onClick={() => { setShowForm(false); resetForm() }} className="text-slate-400 hover:text-slate-600">
              <X size={16} />
            </button>
          </div>

          {/* Type */}
          <div className="flex gap-2">
            {[{ value: 'compagnie', label: 'Compagnie' }, { value: 'client', label: 'Client' }].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(prev => ({
                  ...prev,
                  destinataire_type: opt.value,
                  destinataire_nom: opt.value === 'compagnie' ? (sinistre?.polices?.compagnies?.nom || '') : (sinistre?.tenants?.nom || ''),
                }))}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  form.destinataire_type === opt.value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                }`}
                data-testid={`relance-type-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Date + Destinataire */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-indigo-600 mb-0.5 font-medium">Date de relance</label>
              <input
                type="date"
                required
                min={today}
                value={form.prochaine_relance}
                onChange={e => setForm(prev => ({ ...prev, prochaine_relance: e.target.value }))}
                className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white"
                data-testid="relance-date-input"
              />
            </div>
            <div>
              <label className="block text-[10px] text-indigo-600 mb-0.5 font-medium">Destinataire</label>
              <input
                type="text"
                required
                value={form.destinataire_nom}
                onChange={e => setForm(prev => ({ ...prev, destinataire_nom: e.target.value }))}
                className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white"
                placeholder="Nom"
                data-testid="relance-dest-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] text-indigo-600 mb-0.5 font-medium">Email destinataire</label>
            <input
              type="email"
              value={form.destinataire_email}
              onChange={e => setForm(prev => ({ ...prev, destinataire_email: e.target.value }))}
              className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white"
              placeholder="email@exemple.com"
              data-testid="relance-email-input"
            />
          </div>

          {/* AI draft + Subject + Content */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDraftAI}
              disabled={draftingAI}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50"
              data-testid="relance-draft-ia-btn"
            >
              {draftingAI ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Rediger avec l'IA
            </button>
            <span className="text-[10px] text-slate-400">ou redigez manuellement</span>
          </div>

          <div>
            <label className="block text-[10px] text-indigo-600 mb-0.5 font-medium">Objet</label>
            <input
              type="text"
              value={form.sujet}
              onChange={e => setForm(prev => ({ ...prev, sujet: e.target.value }))}
              className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white"
              placeholder="Objet de la relance"
              data-testid="relance-sujet-input"
            />
          </div>
          <div>
            <label className="block text-[10px] text-indigo-600 mb-0.5 font-medium">Contenu</label>
            <textarea
              value={form.contenu}
              onChange={e => setForm(prev => ({ ...prev, contenu: e.target.value }))}
              rows={5}
              className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white resize-none"
              placeholder="Contenu de l'email..."
              data-testid="relance-contenu-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setShowForm(false); resetForm() }} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">Annuler</button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="relance-save-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
              Planifier
            </button>
          </div>
        </form>
      )}

      {/* Relances list */}
      {(!relances || relances.length === 0) && !showForm && (
        <div className="text-center py-6">
          <Bell size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-400">Aucune relance planifiee</p>
        </div>
      )}

      {(relances || []).map(r => {
        const cfg = STATUT_CONFIG[r.statut] || STATUT_CONFIG.a_envoyer
        const isOverdue = r.prochaine_relance && r.prochaine_relance <= today && r.statut === 'a_envoyer'
        return (
          <RelanceCard key={r.id} r={r} cfg={cfg} isOverdue={isOverdue} onUpdateStatut={onUpdateStatut} />
        )
      })}
    </div>
  )
}


/* ─── V2: Mode de gestion & Impact conservation ─── */
const MODE_GESTION_CONFIG = {
  assurance: { label: 'Assurance', desc: 'Déclaré à l\'assurance — franchise = conservation', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  amiable: { label: 'Amiable', desc: 'Hors assurance — économie vs IRSA', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  retention: { label: 'Rétention', desc: 'Auto-assuré — hors budget conservation', color: 'bg-amber-100 text-amber-700 border-amber-200' },
}

function ModeGestionBlock({ sinistre, onRefresh }) {
  const [editingMode, setEditingMode] = useState(false)
  const [savingMode, setSavingMode] = useState(false)

  const modeGestion = sinistre?.mode_gestion || (sinistre?.est_amiable ? 'amiable' : null)
  const config = modeGestion ? MODE_GESTION_CONFIG[modeGestion] : null

  async function handleSetMode(mode) {
    setSavingMode(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode_gestion: mode, est_amiable: mode === 'amiable' }),
      })
      if (!res.ok) throw new Error('Erreur')
      toast.success(`Mode de gestion : ${MODE_GESTION_CONFIG[mode].label}`)
      setEditingMode(false)
      onRefresh()
    } catch (e) {
      toast.error('Erreur')
    } finally {
      setSavingMode(false)
    }
  }

  return (
    <div className={`pb-3 border-b border-slate-100 ${!modeGestion && !editingMode ? 'ring-1 ring-red-200 rounded-lg p-2 bg-red-50/30' : ''}`} data-testid="mode-gestion-block">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-700">Mode de gestion</span>
        <button
          onClick={() => setEditingMode(!editingMode)}
          className="text-xs text-indigo-500 hover:text-indigo-700"
          data-testid="edit-mode-gestion-btn"
        >
          {editingMode ? 'Annuler' : 'Modifier'}
        </button>
      </div>

      {editingMode ? (
        <div className="space-y-1.5">
          {Object.entries(MODE_GESTION_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleSetMode(key)}
              disabled={savingMode}
              className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                modeGestion === key ? cfg.color : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`mode-gestion-${key}`}
            >
              <p className="font-medium">{cfg.label}</p>
              <p className="text-[10px] opacity-70 mt-0.5">{cfg.desc}</p>
            </button>
          ))}
        </div>
      ) : config ? (
        <div className={`px-3 py-2 rounded-lg border text-xs ${config.color}`}>
          <p className="font-medium">{config.label}</p>
          <p className="text-[10px] opacity-70 mt-0.5">{config.desc}</p>
        </div>
      ) : (
        <p className="text-xs text-red-400 italic">Non defini — a renseigner</p>
      )}
    </div>
  )
}
